import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Box, Button, Card, Icon, InputSelect, InputToggle, Link, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import slack from "~/assets/i/apps/slack_logo.svg";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { getSlackSpaceUrl, handleError, helpers } from "~/lib/helpers";
import toastsActionCreators from "~/store/toasts/actions";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const recommendedChannel = name => {
  const terms = ["dev", "eng", "prod", "product", "engineering", "develop", "customer", "feedback", "release", "ship"];
  return terms.some(term => name.includes(term));
};
const mapChannelToOption = channel => ({
  id: channel.id,
  pre: channel.isMember ? " Joyn is present" : " No Joyn",
  value: `${channel.isMpim ? channel.purpose : `#${channel.name}`}${channel.isPrivate ? " (private)" : ""}`,
  post: helpers.mapSlackChannelTypes(channel),
  disabled: channel.isMember,
  selected: channel.isMember
});
const SlackIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const [isExpanded, setIsExpanded] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelsError, setChannelsError] = useState();
  const [addingError, setAddingError] = useState();
  const error = channelsError || addingError;
  const recommendedChannels = channels.filter(channel => !selectedChannels.includes(channel.id) && !channel.isMember && recommendedChannel(channel.name)).slice(0, 10);
  const fetchSlackChannels = () => {
    setChannelsLoading(true);
    api.fetchSlackChannels().then(response => {
      setChannels(response.filter(channel => !channel.isPrivate));
    }).catch(e => setChannelsError(handleError(e))).finally(() => {
      setChannelsLoading(false);
    });
  };
  useEffect(() => {
    if (slackIntegration) {
      fetchSlackChannels();
    }
  }, [!!slackIntegration]);
  const handleToggle = value => {
    if (value) {
      api.slackInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Slack", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
      mixpanel.track(`Slack Integration – Install`);
    } else if (slackIntegration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(slackIntegration.id));
      mixpanel.track(`Slack Integration – Uninstall`);
    }
  };
  const handleChannelClick = id => {
    if (selectedChannels.includes(id)) {
      setSelectedChannels(prev => prev.filter(p => p !== id));
    } else {
      setSelectedChannels(prev => [...prev, id]);
    }
  };
  const handleAddChannels = () => {
    setAddingLoading(true);
    api.addBotToSlackChannels(selectedChannels).then(() => {
      dispatch(toastsActionCreators.pushed({
        severity: "success",
        message: t("SlackIntegrationCard.botAddedToChannels", {
          count: selectedChannels.length
        }),
        timeout: 10000
      }));
      setSelectedChannels([]);
      fetchSlackChannels();
    }).catch(e => setAddingError(handleError(e))).finally(() => {
      setAddingLoading(false);
    });
    mixpanel.track(`Slack Integration – Select Channels`, {
      selectedChannels
    });
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && slackIntegration ? () => setIsExpanded(prev => !prev) : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: slack,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Slack"), slackIntegration && userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE]) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      display: "inline-block",
      value: !!slackIntegration,
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf([AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE])
    })),
    collapsed: !slackIntegration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), slackIntegration && /*#__PURE__*/React.createElement(Box, {
    onClick: e => e.stopPropagation(),
    fullWidth: true
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    s: {
      mb: 3
    }
  }, t("CompanyIntegrations.connectedTo"), " ", /*#__PURE__*/React.createElement(Link, {
    href: getSlackSpaceUrl(slackIntegration),
    underline: true
  }, slackIntegration.token.team?.name), " ", t("CompanyIntegrations.workspace"), ". Select the channel(s) you want us to summarize and map:"), channelsLoading ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 6
    },
    center: true
  }) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    s: {
      mb: 4
    },
    fullWidth: true
  }, !!recommendedChannels.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "bold",
    display: "block",
    s: {
      mb: 2
    }
  }, "Recommended by Joyn"), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      mb: 5
    }
  }, recommendedChannels.map(channel => /*#__PURE__*/React.createElement(Button, {
    key: channel.id,
    onClick: () => handleChannelClick(channel.id),
    color: selectedChannels.includes(channel.id) ? "primary" : "grey",
    fontSize: 3,
    s: {
      px: 2,
      py: 0.5
    },
    outline: false,
    rounded: true
  }, mapChannelToOption(channel).value)))), /*#__PURE__*/React.createElement(InputSelect, {
    value: selectedChannels,
    onChange: value => setSelectedChannels(value),
    options: channels.map(channel => mapChannelToOption(channel)),
    placeholder: "Select channels",
    multiple: true,
    withSearch: true,
    fullSelections: true,
    fullWidth: true
  })), /*#__PURE__*/React.createElement(Box, {
    gap: 3,
    fullWidth: true,
    justify: "end"
  }, !!selectedChannels.length && /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    onClick: () => setSelectedChannels([]),
    fontSize: 3.5,
    s: {
      px: 4,
      py: 2
    },
    outline: false
  }, "Clear selection"), /*#__PURE__*/React.createElement(Button, {
    loading: addingLoading,
    onClick: handleAddChannels,
    disabled: !selectedChannels.length,
    fontSize: 3.5,
    s: {
      px: 4,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    s: {
      mt: -0.5,
      mr: 1
    }
  }), "Add Joyn to channels"))));
};
export default SlackIntegrationCard;